import { Controller } from '@hotwired/stimulus'
import Dashboard from '@uppy/dashboard'

import { uppyS3StorageInstance, uppyLocalStorageInstance, uploadedS3FileData, uploadedLocalFileData } from '../src/mixins/uppy'

export default class extends Controller {
  static targets = ['result', 'uploadButton', 'dashboard']
  static values = {
    server: Boolean
  }

  connect () {
    this.uppy = this.createUppy()
  }

  disconnect () {
    if (this.uppy) {
      this.uppy.destroy()
      this.uppy = null
    }
  }

  createUppy () {
    const uppyId = this.dashboardTarget.id
    let uppy

    if (this.serverValue) {
      uppy = uppyS3StorageInstance(uppyId)
        .on('upload-success', (file, response) => {
          // Store the uploaded file data
          const fileData = uploadedS3FileData(file, response)
          this.submitFileData(fileData)
        })
    } else {
      uppy = uppyLocalStorageInstance(uppyId)
        .on('upload-success', (file, response) => {
          // Store the uploaded file data
          const fileData = uploadedLocalFileData(file, response)
          this.submitFileData(fileData)
        })
    }

    // Allowed file types for media uploads:
    // - Image: jpg, jpeg, dng (drone camera formats), tiff (professional)
    // - Video: mp4, mov (common drone/robot video formats)
    // - .NAV: satellite positioning data
    // - .OBS: raw GNSS observation logs
    // - .BIN: flight log that stores low-level system data
    // - .MRK: timestamp and position (GPS co-ordinates) for each photo captured
    // - .DAT: These are deep-dive logs that track everything the drone does in real-time
    // - .RTK: RTK data files used for high-precision positioning
    uppy.setOptions({
      restrictions: {
        allowedFileTypes: ['image/jpg', 'image/jpeg', 'image/dng', 'image/tiff', 'video/mp4', 'video/quicktime', '.nav', '.obs', '.bin', '.mrk', '.dat', '.rtk']
      }
    })

    uppy.use(Dashboard, {
      target: '#' + uppyId,
      inline: true,
      height: 550,
      width: '100%',
      showProgressDetails: true,
      note: 'Drag and drop your flight media files here, or click to browse. Supports images (jpg, jpeg, dng, tiff), videos (mp4, mov), and drone data files (.nav, .obs, .bin, .mrk, .dat, .rtk).',
      proudlyDisplayPoweredByUppy: false
    })

    uppy.on('upload-error', (file, error) => {
      console.error('Upload error:', error)
    })

    uppy.on('complete', (result) => {
      console.log('Upload complete:', result)
    })

    return uppy
  }

  // fileData example:
  // '{"id":"f3a7432e4c0592f0d2d16970bd24b1a7.png","storage":"cache","metadata":{"size":1801768,"filename":"DJI_001.png","mime_type":"image/png"}}'
  submitFileData (fileData) {
    // Get the form element to extract the action URL
    const csrfToken = this.element.querySelector('input[name="authenticity_token"]')?.value
    const actionUrl = this.element.getAttribute('action')
    const formData = new FormData()
    const { id, storage, metadata = {} } = JSON.parse(fileData)
    formData.append('device_media[file][id]', id)
    formData.append('device_media[file][storage]', storage)
    formData.append('device_media[file][metadata][size]', metadata.size ?? '')
    formData.append('device_media[file][metadata][filename]', metadata.filename ?? '')
    formData.append('device_media[file][metadata][mime_type]', metadata.mime_type ?? '')

    // Send AJAX request to create device media
    fetch(actionUrl, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken
      }
    })
      .then(response => {
        if (!response.ok) {
          console.error('Failed to create device media:', response.statusText)
        }
      })
      .catch(error => {
        console.error('Error creating device media:', error)
      })
  }
}
